import { FUNCTION_ROLE_LABELS } from '@projektverwaltung/shared';
import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Navigate, useLocation } from 'react-router-dom';
import { LanguageSwitcher } from '../components/language-switcher.js';
import { type DevUser, api, authLoginUrl } from '../lib/api.js';
import { devLoginState } from '../lib/dev-login.js';
import { useSession } from '../lib/session.js';

export function LoginPage() {
  const { t } = useTranslation();
  const { user, signInAs } = useSession();
  const location = useLocation();
  const from = (location.state as { from?: string } | null)?.from ?? '/';
  // #116: Der Azure-Callback schickt ein ausgeschiedenes Konto mit ?error=account_inactive hierher,
  // eine Adresse, die noch an einem aktiven Alt-Konto hängt, mit ?error=account_conflict.
  // Nur bekannte Codes werden angezeigt (kein Text aus der URL).
  const fehlerCode = new URLSearchParams(location.search).get('error');
  const fehlerText =
    fehlerCode === 'account_inactive'
      ? t('login.error_account_inactive', 'Dein Konto ist deaktiviert. Wende dich an einen Admin.')
      : fehlerCode === 'account_conflict'
        ? t(
            'login.error_account_conflict',
            'Dein Konto ist noch einem alten Eintrag zugeordnet. Ein Admin muss den alten Nutzer deaktivieren – danach kannst du dich anmelden.',
          )
        : null;

  // retry: false — ein 404 dev_login_disabled ist eine endgültige Antwort. Mit Retry bliebe
  // die Abfrage im Hintergrund-Tab sogar dauerhaft pending (react-query pausiert Retries,
  // solange das Fenster nicht fokussiert ist) und im Vordergrund blitzte der Block ~1 s auf.
  const devUsersQuery = useQuery({
    queryKey: ['dev-users'],
    queryFn: api.listDevUsers,
    retry: false,
  });
  // Azure-AD aktiv → Server antwortet 404 dev_login_disabled → Dev-Block komplett weg (Prod).
  // Erst rendern, wenn der Zustand geklärt ist — kein Aufblitzen auf Prod.
  const devLogin = devLoginState(devUsersQuery);
  const showDevBlock = devLogin === 'available' || devLogin === 'unreachable';
  const [selected, setSelected] = useState<string>('');

  if (user) return <Navigate to={from} replace />;

  const onLogin = () => {
    const u = devUsersQuery.data?.items.find((x) => x.user_id === selected);
    if (!u) return;
    signInAs({ ...u, mode: 'dev-stub' });
  };

  return (
    <div className="min-h-screen flex flex-col bg-ink-50 relative overflow-hidden">
      <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-brand-yellow/20 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-32 -left-32 w-96 h-96 rounded-full bg-brand-orange/10 blur-3xl pointer-events-none" />

      <header className="flex justify-between items-center px-8 py-6 relative z-10">
        <img src="/logo-h32.png" alt="mindsquare" className="h-7 w-auto" />
        <LanguageSwitcher />
      </header>

      <main className="flex-1 flex items-center justify-center px-4 relative z-10">
        <div className="w-full max-w-md animate-slide-up">
          <div className="text-center mb-8">
            <img src="/logo-h48.png" alt="mindsquare" className="h-12 w-auto mx-auto mb-6" />
            <h1 className="font-display text-3xl font-semibold text-ink-900 tracking-tight">
              {t('app.title', 'Projektverwaltung')}
            </h1>
            <p className="text-sm text-ink-500 mt-2">{t('login.subtitle')}</p>
          </div>

          <div className="bg-white rounded-2xl border border-ink-200 shadow-lift p-8 space-y-5">
            {fehlerText && (
              <p
                role="alert"
                className="text-sm p-3 rounded-lg border border-[color:var(--danger)] text-[color:var(--danger)]"
              >
                {fehlerText}
              </p>
            )}
            <button
              type="button"
              onClick={() => {
                window.location.href = authLoginUrl(from);
              }}
              className="w-full px-4 py-3 rounded-lg bg-ink-900 text-white text-sm font-medium hover:opacity-90 cursor-pointer flex items-center justify-center gap-2"
              title={t('login.azure_hint', 'Anmeldung über das mindsquare Microsoft-365-Konto')}
            >
              <svg width="18" height="18" viewBox="0 0 23 23" fill="none">
                <rect x="1" y="1" width="10" height="10" fill="#F35325" />
                <rect x="12" y="1" width="10" height="10" fill="#81BC06" />
                <rect x="1" y="12" width="10" height="10" fill="#05A6F0" />
                <rect x="12" y="12" width="10" height="10" fill="#FFBA08" />
              </svg>
              {t('login.azure_ad')}
            </button>

            {showDevBlock && (
              <>
                <div className="relative">
                  <div className="absolute inset-0 flex items-center">
                    <div className="w-full border-t border-ink-200" />
                  </div>
                  <div className="relative flex justify-center">
                    <span className="bg-white px-3 text-[10px] uppercase tracking-widest text-ink-400 font-medium">
                      {t('login.or')}
                    </span>
                  </div>
                </div>

                <div className="space-y-3">
                  <div className="flex items-start gap-2 p-3 rounded-lg bg-brand-yellow-soft border border-brand-yellow/40">
                    <svg
                      className="w-4 h-4 text-brand-orange-deep shrink-0 mt-0.5"
                      viewBox="0 0 20 20"
                      fill="currentColor"
                    >
                      <path
                        fillRule="evenodd"
                        d="M8.485 2.495c.673-1.167 2.357-1.167 3.03 0l6.28 10.875c.673 1.167-.17 2.625-1.516 2.625H3.72c-1.347 0-2.189-1.458-1.515-2.625L8.485 2.495zM10 6a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0v-3.5A.75.75 0 0110 6zm0 9a1 1 0 100-2 1 1 0 000 2z"
                        clipRule="evenodd"
                      />
                    </svg>
                    <p className="text-xs text-ink-700 leading-relaxed">
                      {t(
                        'login.dev_warning_box',
                        'Lokaler Dev-Login ohne Passwort. Wird automatisch deaktiviert, sobald Azure-AD aktiv ist.',
                      )}
                    </p>
                  </div>

                  <label className="block">
                    <span className="text-[11px] uppercase tracking-wide text-ink-500 font-medium block mb-1.5">
                      {t('login.select_test_user', 'Test-User auswählen')}
                    </span>
                    <select
                      value={selected}
                      onChange={(e) => setSelected(e.target.value)}
                      className="w-full px-3 py-2.5 rounded-lg border border-ink-200 bg-white text-sm focus:border-brand-orange focus:outline-none focus:ring-2 focus:ring-brand-orange/20"
                    >
                      <option value="">{t('login.choose_placeholder', '— bitte wählen —')}</option>
                      {devUsersQuery.data?.items.map((u) => (
                        <option key={u.user_id} value={u.user_id}>
                          {formatDevUserOption(u, {
                            admin: t('common.admin', 'Admin'),
                            viewer: t('common.viewer', 'Betrachter'),
                          })}
                        </option>
                      ))}
                    </select>
                  </label>

                  {devLogin === 'unreachable' && (
                    <p className="text-xs text-red-700">
                      {t('login.api_unreachable', 'API nicht erreichbar — läuft `pnpm dev`?')}
                    </p>
                  )}

                  <button
                    type="button"
                    onClick={onLogin}
                    disabled={!selected}
                    className="w-full px-4 py-3 rounded-lg bg-brand-orange text-white text-sm font-medium hover:bg-brand-orange-deep disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
                  >
                    {t('login.dev_signin_btn', 'Als ausgewählten User einloggen')}
                  </button>
                </div>
              </>
            )}
          </div>

          <p className="text-center text-xs text-ink-400 mt-6">
            {showDevBlock
              ? t('login.footer', 'mindsquare AG · Internes Tool · Dev-Modus')
              : t('login.footer_prod', 'mindsquare AG · Internes Tool')}
          </p>
        </div>
      </main>
    </div>
  );
}

// Helper läuft außerhalb des React-Scopes (kein t() direkt) → übersetzte Labels
// werden vom aufrufenden Component (das useTranslation hat) reingereicht.
function formatDevUserOption(u: DevUser, labels: { admin: string; viewer: string }): string {
  if (u.function_roles.length === 0) {
    // Betrachter = keine Funktionsrolle, kein Admin; Admin = is_admin
    const roleLabel = u.is_admin ? labels.admin : labels.viewer;
    return `${u.name}  ·  ${roleLabel}`;
  }
  const roles = u.function_roles
    .map((r) => {
      const label = FUNCTION_ROLE_LABELS[r.role];
      return r.scope_segment ? `${label} (${r.scope_segment})` : label;
    })
    .join(', ');
  const adminSuffix = u.is_admin ? ` · ${labels.admin}` : '';
  return `${u.name}  ·  ${roles}${adminSuffix}`;
}
